// TwoFactorAuth.jsx
import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Flex,
  Button,
  CloseButton,
  Heading,
  Input,
  Text,
  useStyleConfig,
  useTheme,
  useColorMode,
} from "@chakra-ui/react";
import { Auth, cqAPI } from "../../lib/Auth";
import LogoComponent from "../Logo/LogoComponent";

export default function TwoFactorAuth(
  {
    isAlertVisible,
    alertMessage,
    alertStatus,
    showAlert,
    hideAlert
  }
) {
  const isPartner = window.location.hostname.includes('adec') || window.location.hostname.includes('a-dec');
  const modalBoxStyles = useStyleConfig('ModalBox', { variant: 'solid' });

  const {colorMode} = useColorMode();
  const theme = useTheme();
  const link_color = colorMode === "light" ? theme.colors.brand[300] : theme.colors.lightBackground;
  const location = useLocation();
  const navigate = useNavigate();

  // Grab the UID and email passed from the login page
  const { uid, email, password, trustDevice, redirectTo } = location.state || {};

  // Store each digit in an array, e.g. ["", "", "", "", "", ""]
  const [codeDigits, setCodeDigits] = useState(Array(6).fill(""));
  const inputRefs = useRef([]);

  // a resend in flight; the control is disabled until it answers
  const [isResending, setIsResending] = useState(false);

  // New state for controlling the support pop-up and email copy status
  const [isSupportPopupOpen, setIsSupportPopupOpen] = useState(false);
  const [isEmailCopied, setIsEmailCopied] = useState(false);
  const supportEmail = isPartner ? "Info@linkforadec.com" : "support@getviva.ai";

  const handleVerify = async (completeCode) => {
    try {
      // The code alone no longer proves anything: /verify-2fa wants the pending-login
      // credential the password step handed back, which Auth kept in memory (SS-4131).
      //
      // Send it when we have one, and submit WITHOUT it when we do not, rather than
      // refusing to ask. An api that has not shipped this change yet issues no such
      // credential and verifies the code on its own — refusing here would strand every
      // sign-in against it, which is precisely what happened when the two repos were
      // out of step. Deciding whether a code is good enough is the api's job, and the
      // api that does require this rejects a credential-less call before it counts an
      // attempt or spends any budget, so asking costs nothing.
      const pendingLoginToken = Auth.pendingLogin(uid);
      const response = await cqAPI.post(
        "/verify-2fa?uid="+uid+"&code="+completeCode+"&trustDevice="+trustDevice,
        pendingLoginToken ? { pendingLoginToken } : undefined
      );

      if (response.type === "ok") {
        // 2FA success. The challenge is spent; the credential cannot be reused.
        Auth.clearPendingLogin();
        Auth.storeUser(response.data.user);
        if (response.data.rememberMeToken) {
          localStorage.setItem('rememberMeToken', response.data.rememberMeToken);
        }
        const storedRedirect = localStorage.getItem("post2faRedirect");
        const nextPath = (typeof redirectTo === "string" && redirectTo.startsWith("/"))
          ? redirectTo
          : ((typeof storedRedirect === "string" && storedRedirect.startsWith("/")) ? storedRedirect : "/");
        localStorage.removeItem("post2faRedirect");
        navigate(nextPath);
        hideAlert();
      } else {
        showAlert(response.msg || "Code is incorrect or expired.");
      }
    } catch (err) {
      console.log(err.message);
      showAlert(err.message || "Something went wrong.");
    }
  };

  useEffect(() => {
    const completeCode = codeDigits.join("");
    if (completeCode.length === 6){
      handleVerify(completeCode);
    }
    else {
      hideAlert();
    }
  }, [codeDigits]);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const newDigits = [...codeDigits];
    newDigits[index] = value;
    setCodeDigits(newDigits);

    if (value && index < 5) {
      inputRefs.current[index+1].focus();
    }
  }

  const handleDelete = (index, e) => {
    if (e.key === "Backspace" && codeDigits[index] === "" && index > 0) {
      inputRefs.current[index-1].focus();
    }
  }

  // Handle pasting the full 6-digit code into any input
  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("Text").trim();
    // Only update if pasted data is exactly 6 digits
    if (/^\d{6}$/.test(pasteData)) {
      const digitsArray = pasteData.split("");
      setCodeDigits(digitsArray);
      // Focus the last input
      inputRefs.current[5]?.focus();
    }
  };

  const maskEmail = (email) => {
    if (!email) return "";
    const [local, domain] = email.split("@");
    if (local.length < 3) return email.toLowerCase();
    return local.charAt(0).toLowerCase() + "xxxx" + local.slice(-2).toLowerCase() + "@" + domain.toLowerCase();
  }

  const resendEmail = async () => {
    // One at a time. Each request issues a NEW challenge that retires the previous
    // one, so two in flight can land out of order and leave the OLDER credential held
    // against the newer code — and every request spends the account's issuance
    // budget, which is five in fifteen minutes (SS-4131).
    if (isResending) {
      return;
    }
    setIsResending(true);
    try {
      const response = await cqAPI.post("/login", {email: email, password:password});

      // keep the credential the new challenge came with, or the next code cannot be
      // verified
      Auth.rememberPendingLogin(response);

      if (response.type === "2fa_required") {
        showAlert("If an account exists for the provided email a new verification will be sent.", "success");
      } else {
        showAlert(response.msg || "Code is incorrect or expired.");
      }
    } catch (err) {
      console.log(err.message);
      showAlert(err.message || "Something went wrong.");
    } finally {
      setIsResending(false);
    }
  }

  // Function to handle the "Contact support" button
  const handleContactSupport = () => {
    setIsSupportPopupOpen(true);
  };

  const handleCopySupportEmail = () => {

    navigator.clipboard?.writeText(supportEmail)
      .then(() => {
        setIsEmailCopied(true);
        // Reset the copy status after 2 seconds
        setTimeout(() => {
          setIsEmailCopied(false);
        }, 2000);
      })
      .catch((err) => {
        console.error("Failed to copy email: ", err);
      });
  };


  return (
    <Flex minH="100vh" justifyContent="center" alignItems="center">
    <Box
      p={8}
      minWidth={{base: "90%", sm: "400px", md: "500px", lg: "500px"}}
      maxWidth={{base: "90%", sm: "540px", md: "600px", lg: "800px"}}
      borderWidth={1}
      borderRadius={8}
      boxShadow="lg"
      __css={modalBoxStyles}
    >
      {isAlertVisible && (
        <Alert status={alertStatus}>
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>
              <Text casing="capitalize">{alertStatus}</Text>
            </AlertTitle>
            <AlertDescription display="block">
              {alertMessage}
            </AlertDescription>
          </Box>
            <CloseButton
              position="absolute"
              right="8px"
              top="8px"
              onClick={() => hideAlert()}
            />
        </Alert>
      )}
      <LogoComponent isCollapsed={false} boxSize="5rem" mx="auto" mb={4} ></LogoComponent>
      <Box textAlign="center">
        <Heading
          as="h1"
          fontSize={{base: "20px", md: "20px", lg: "24px"}}
          fontWeight="600"
          lineHeight="140%"
        >
        Verify This Device
        </Heading>
      </Box>

      <Text ml={4} my={8} textAlign="left" maxW="400px">
        For extra security, we sent a verification email to <b>{maskEmail(email)}</b>
        <br/><br/>
        <b> Open your email on this device for verification </b>
        <br/><br/>
        or input the 6-digit code below:
      </Text>

      <Flex justifyContent="space-between" maxW="400px" mx="auto" mb={5}>
        {codeDigits.map((digit, index) => (
          <Input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            className={`tfa_input_${index}`}
            maxLength={1}
            textAlign="center"
            fontSize="2xl"
            w="50px"
            h="50px"
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleDelete(index, e)}
            onPaste={(e) => handlePaste(e)}
          />
        ))}
      </Flex>

      <Flex ml={4} direction="column" align="left" mt={20}>
        <Flex align="center">
          <Text>Didn't get it?</Text>
          <Button
            color={link_color}
            variant="link"
            isDisabled={isResending}
            onClick={() => {resendEmail()}}
          >
            Resend email
          </Button>
        </Flex>
        <Flex align="center">
          <Text>Still having trouble?</Text>
          <Button color={link_color} variant="link" onClick={() => {
            handleContactSupport()
          }}>
            Contact support
          </Button>
        </Flex>
      </Flex>
      </Box>
      {/* Support Pop-up Modal */}
      {isSupportPopupOpen && (
        <Box
          position="fixed"
          top="0"
          left="0"
          w="100vw"
          h="100vh"
          bg={colorMode === "light" ? "rgba(0,0,0,0.5)" : "rgba(0, 0, 0, 0.8)"}
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex="9999"
        >
          <Box
            position="relative"
            p={7}
            borderRadius="md"
            boxShadow="lg"
            maxW="400px"
            bg={colorMode === "light" ? theme.colors.lightBackground : theme.colors.darkBackground}
            color={colorMode === "light" ? theme.colors.lightText : theme.colors.darkText}
            textAlign="center"
          >
            <CloseButton
              position="absolute"
              right="8px"
              bottom="8px"
              top="8px"
              onClick={() => setIsSupportPopupOpen(false)}
            />
            <Text my={4}>
              Please email us and someone from our team will get back to you.
            </Text>
            <Flex justifyContent="center">
              <Button onClick={handleCopySupportEmail} mb={4}>
                {isEmailCopied ? "Copied!" : supportEmail}
              </Button>
            </Flex>
          </Box>
        </Box>
      )}
    </Flex>
  );
}
